import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImagePlus, X, DollarSign, Upload, Loader2, MapPin, Clock, Calendar, Briefcase, Phone, Mail, ExternalLink, Search, Tag, Home, Car } from 'lucide-react';
import { listingsAPI, metaAPI, uploadAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CITIES = ["Pickering", "Ajax", "Whitby", "Courtice", "Oshawa", "Bowmanville"];
const CONDITIONS = [
  { value: "new", label: "New" },
  { value: "like_new", label: "Like New" },
  { value: "good", label: "Good" },
  { value: "fair", label: "Fair" },
  { value: "poor", label: "Poor" },
];

// Categories that don't need price
const NO_PRICE_TYPES = ["lost_found", "announcement"];
// Categories that don't need condition
const NO_CONDITION_TYPES = ["event", "job", "service", "lost_found", "announcement", "rental", "real_estate", "garage_sale"];
// Categories that don't need payment method
const NO_PAYMENT_TYPES = ["lost_found", "announcement", "job", "event"];

function FieldInput({ label, required, children, testId }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">{label}{required && ' *'}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full border border-zinc-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none placeholder:text-zinc-400";
const selectCls = "w-full border border-zinc-200 rounded-lg px-4 py-3 text-sm bg-white focus:ring-2 focus:ring-[#002FA7] focus:outline-none";

function TypeFields({ listingType, typeFields, setTypeField }) {
  const set = (key) => (e) => setTypeField(key, e.target?.value !== undefined ? e.target.value : e);
  const val = (key) => typeFields[key] || '';

  switch (listingType) {
    case 'garage_sale':
      return (
        <div className="bg-white rounded-lg border border-zinc-200 p-6 space-y-4" data-testid="garage-sale-fields">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-600 flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> Garage Sale Details</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Sale Date" required><input type="date" value={val('sale_date')} onChange={set('sale_date')} className={inputCls} data-testid="sale-date" /></FieldInput>
            <FieldInput label="End Date (if multi-day)"><input type="date" value={val('sale_end_date')} onChange={set('sale_end_date')} className={inputCls} data-testid="sale-end-date" /></FieldInput>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Start Time"><input type="time" value={val('sale_time_start')} onChange={set('sale_time_start')} className={inputCls} data-testid="sale-time-start" /></FieldInput>
            <FieldInput label="End Time"><input type="time" value={val('sale_time_end')} onChange={set('sale_time_end')} className={inputCls} data-testid="sale-time-end" /></FieldInput>
          </div>
          <FieldInput label="Sale Address" required><input type="text" value={val('sale_address')} onChange={set('sale_address')} placeholder="Street address for the sale" className={inputCls} data-testid="sale-address" /></FieldInput>
          <FieldInput label="Directions / Notes"><input type="text" value={val('sale_directions')} onChange={set('sale_directions')} placeholder="e.g. Look for signs on the lawn" className={inputCls} data-testid="sale-directions" /></FieldInput>
        </div>
      );

    case 'event':
      return (
        <div className="bg-white rounded-lg border border-zinc-200 p-6 space-y-4" data-testid="event-fields">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-violet-600 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Event Details</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Event Date" required><input type="date" value={val('event_date')} onChange={set('event_date')} className={inputCls} data-testid="event-date" /></FieldInput>
            <FieldInput label="End Date"><input type="date" value={val('event_end_date')} onChange={set('event_end_date')} className={inputCls} data-testid="event-end-date" /></FieldInput>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Start Time"><input type="time" value={val('event_time')} onChange={set('event_time')} className={inputCls} data-testid="event-time" /></FieldInput>
            <FieldInput label="End Time"><input type="time" value={val('event_end_time')} onChange={set('event_end_time')} className={inputCls} data-testid="event-end-time" /></FieldInput>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Venue Name"><input type="text" value={val('venue')} onChange={set('venue')} placeholder="e.g. Oshawa Civic Centre" className={inputCls} data-testid="event-venue" /></FieldInput>
            <FieldInput label="Venue Address"><input type="text" value={val('venue_address')} onChange={set('venue_address')} placeholder="Full address" className={inputCls} data-testid="event-venue-address" /></FieldInput>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Organizer"><input type="text" value={val('organizer')} onChange={set('organizer')} placeholder="Who's hosting?" className={inputCls} data-testid="event-organizer" /></FieldInput>
            <FieldInput label="Ticket URL"><input type="url" value={val('ticket_url')} onChange={set('ticket_url')} placeholder="https://..." className={inputCls} data-testid="event-ticket-url" /></FieldInput>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="event-free-toggle">
              <input type="checkbox" checked={val('is_free') === true || val('is_free') === 'true'} onChange={(e) => setTypeField('is_free', e.target.checked)} className="w-4 h-4 rounded border-zinc-300 text-[#002FA7] focus:ring-[#002FA7]" />
              <span className="text-sm text-zinc-700">This is a free event</span>
            </label>
          </div>
        </div>
      );

    case 'job':
      return (
        <div className="bg-white rounded-lg border border-zinc-200 p-6 space-y-4" data-testid="job-fields">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-amber-600 flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> Job Details</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Company / Employer"><input type="text" value={val('company_name')} onChange={set('company_name')} placeholder="Company name" className={inputCls} data-testid="job-company" /></FieldInput>
            <FieldInput label="Job Type">
              <select value={val('job_type')} onChange={set('job_type')} className={selectCls} data-testid="job-type">
                <option value="">Select...</option>
                <option value="full_time">Full-Time</option>
                <option value="part_time">Part-Time</option>
                <option value="contract">Contract</option>
                <option value="freelance">Freelance</option>
                <option value="day_labour">Day Labour</option>
                <option value="internship">Internship</option>
                <option value="volunteer">Volunteer</option>
              </select>
            </FieldInput>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <FieldInput label="Salary Min ($)"><input type="number" min="0" value={val('salary_min')} onChange={set('salary_min')} placeholder="e.g. 20" className={inputCls} data-testid="job-salary-min" /></FieldInput>
            <FieldInput label="Salary Max ($)"><input type="number" min="0" value={val('salary_max')} onChange={set('salary_max')} placeholder="e.g. 35" className={inputCls} data-testid="job-salary-max" /></FieldInput>
            <FieldInput label="Pay Period">
              <select value={val('salary_period')} onChange={set('salary_period')} className={selectCls} data-testid="job-salary-period">
                <option value="">Select...</option>
                <option value="hourly">Per Hour</option>
                <option value="daily">Per Day</option>
                <option value="weekly">Per Week</option>
                <option value="monthly">Per Month</option>
                <option value="yearly">Per Year</option>
                <option value="fixed">Fixed Price</option>
              </select>
            </FieldInput>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Apply Email"><input type="email" value={val('apply_email')} onChange={set('apply_email')} placeholder="hiring@company.com" className={inputCls} data-testid="job-apply-email" /></FieldInput>
            <FieldInput label="Apply URL"><input type="url" value={val('apply_url')} onChange={set('apply_url')} placeholder="https://..." className={inputCls} data-testid="job-apply-url" /></FieldInput>
          </div>
          <FieldInput label="Remote Option">
            <select value={val('remote_option')} onChange={set('remote_option')} className={selectCls} data-testid="job-remote">
              <option value="">Select...</option>
              <option value="on_site">On-Site</option>
              <option value="remote">Remote</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </FieldInput>
        </div>
      );

    case 'service':
      return (
        <div className="bg-white rounded-lg border border-zinc-200 p-6 space-y-4" data-testid="service-fields">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-blue-600 flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> Service Details</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Service Area"><input type="text" value={val('service_area')} onChange={set('service_area')} placeholder="e.g. Durham Region, GTA" className={inputCls} data-testid="service-area" /></FieldInput>
            <FieldInput label="Hourly Rate ($)"><input type="number" min="0" step="0.01" value={val('hourly_rate')} onChange={set('hourly_rate')} placeholder="0.00" className={inputCls} data-testid="service-rate" /></FieldInput>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Years of Experience"><input type="number" min="0" value={val('experience_years')} onChange={set('experience_years')} placeholder="0" className={inputCls} data-testid="service-experience" /></FieldInput>
            <FieldInput label="Availability"><input type="text" value={val('availability')} onChange={set('availability')} placeholder="e.g. Weekdays 9-5, Evenings" className={inputCls} data-testid="service-availability" /></FieldInput>
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="service-licensed">
            <input type="checkbox" checked={val('license_certified') === true || val('license_certified') === 'true'} onChange={(e) => setTypeField('license_certified', e.target.checked)} className="w-4 h-4 rounded border-zinc-300 text-[#002FA7] focus:ring-[#002FA7]" />
            <span className="text-sm text-zinc-700">Licensed / Certified professional</span>
          </label>
        </div>
      );

    case 'lost_found':
      return (
        <div className="bg-white rounded-lg border border-zinc-200 p-6 space-y-4" data-testid="lost-found-fields">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-red-600 flex items-center gap-1"><Search className="w-3.5 h-3.5" /> Lost & Found Details</p>
          <FieldInput label="Lost or Found" required>
            <div className="flex gap-3">
              {['lost', 'found'].map(v => (
                <button key={v} type="button" onClick={() => setTypeField('lost_or_found', v)}
                  className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${val('lost_or_found') === v ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300'}`}
                  data-testid={`lf-${v}-btn`}
                >{v === 'lost' ? 'Lost' : 'Found'}</button>
              ))}
            </div>
          </FieldInput>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Date Lost/Found"><input type="date" value={val('date_occurred')} onChange={set('date_occurred')} className={inputCls} data-testid="lf-date" /></FieldInput>
            <FieldInput label="Location"><input type="text" value={val('location_detail')} onChange={set('location_detail')} placeholder="Where was it lost/found?" className={inputCls} data-testid="lf-location" /></FieldInput>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Item/Pet Type"><input type="text" value={val('item_type')} onChange={set('item_type')} placeholder="e.g. Dog, Keys, Wallet" className={inputCls} data-testid="lf-type" /></FieldInput>
            <FieldInput label="Contact Phone"><input type="tel" value={val('contact_phone')} onChange={set('contact_phone')} placeholder="(905) 555-1234" className={inputCls} data-testid="lf-phone" /></FieldInput>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Pet Name (if applicable)"><input type="text" value={val('pet_name')} onChange={set('pet_name')} placeholder="Pet's name" className={inputCls} data-testid="lf-pet-name" /></FieldInput>
            <FieldInput label="Reward Offered"><input type="text" value={val('reward')} onChange={set('reward')} placeholder="e.g. $50 reward" className={inputCls} data-testid="lf-reward" /></FieldInput>
          </div>
        </div>
      );

    case 'announcement':
      return (
        <div className="bg-white rounded-lg border border-zinc-200 p-6 space-y-4" data-testid="announcement-fields">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-orange-600 flex items-center gap-1">Community Announcement Details</p>
          <FieldInput label="Announcement Type">
            <select value={val('announcement_type')} onChange={set('announcement_type')} className={selectCls} data-testid="ann-type">
              <option value="">Select...</option>
              <option value="news">Community News</option>
              <option value="alert">Safety Alert</option>
              <option value="notice">Public Notice</option>
              <option value="watch">Neighbourhood Watch</option>
              <option value="shoutout">Thank You / Shout-Out</option>
            </select>
          </FieldInput>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Effective Date"><input type="date" value={val('effective_date')} onChange={set('effective_date')} className={inputCls} data-testid="ann-effective" /></FieldInput>
            <FieldInput label="Expiry Date"><input type="date" value={val('expiry_date')} onChange={set('expiry_date')} className={inputCls} data-testid="ann-expiry" /></FieldInput>
          </div>
        </div>
      );

    case 'rental':
      return (
        <div className="bg-white rounded-lg border border-zinc-200 p-6 space-y-4" data-testid="rental-fields">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-teal-600 flex items-center gap-1"><Home className="w-3.5 h-3.5" /> Rental Details</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <FieldInput label="Bedrooms"><input type="number" min="0" value={val('bedrooms')} onChange={set('bedrooms')} placeholder="0" className={inputCls} data-testid="rental-bedrooms" /></FieldInput>
            <FieldInput label="Bathrooms"><input type="number" min="0" step="0.5" value={val('bathrooms')} onChange={set('bathrooms')} placeholder="0" className={inputCls} data-testid="rental-bathrooms" /></FieldInput>
            <FieldInput label="Sq. Ft."><input type="number" min="0" value={val('square_feet')} onChange={set('square_feet')} placeholder="0" className={inputCls} data-testid="rental-sqft" /></FieldInput>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Available Date"><input type="date" value={val('available_date')} onChange={set('available_date')} className={inputCls} data-testid="rental-available" /></FieldInput>
            <FieldInput label="Lease Term">
              <select value={val('lease_term')} onChange={set('lease_term')} className={selectCls} data-testid="rental-lease">
                <option value="">Select...</option>
                <option value="month_to_month">Month-to-Month</option>
                <option value="6_months">6 Months</option>
                <option value="1_year">1 Year</option>
                <option value="2_years">2 Years</option>
              </select>
            </FieldInput>
          </div>
          <div className="flex flex-wrap gap-4">
            {[{key:'pet_friendly', label:'Pet Friendly'}, {key:'parking', label:'Parking Included'}, {key:'laundry', label:'In-Unit Laundry'}, {key:'utilities_included', label:'Utilities Included'}].map(f => (
              <label key={f.key} className="flex items-center gap-2 cursor-pointer" data-testid={`rental-${f.key}`}>
                <input type="checkbox" checked={val(f.key) === true || val(f.key) === 'true'} onChange={(e) => setTypeField(f.key, e.target.checked)} className="w-4 h-4 rounded border-zinc-300 text-[#002FA7] focus:ring-[#002FA7]" />
                <span className="text-sm text-zinc-700">{f.label}</span>
              </label>
            ))}
          </div>
        </div>
      );

    case 'vehicle':
      return (
        <div className="bg-white rounded-lg border border-zinc-200 p-6 space-y-4" data-testid="vehicle-fields">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-indigo-600 flex items-center gap-1"><Car className="w-3.5 h-3.5" /> Vehicle Details</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <FieldInput label="Make"><input type="text" value={val('make')} onChange={set('make')} placeholder="e.g. Toyota" className={inputCls} data-testid="vehicle-make" /></FieldInput>
            <FieldInput label="Model"><input type="text" value={val('model')} onChange={set('model')} placeholder="e.g. Camry" className={inputCls} data-testid="vehicle-model" /></FieldInput>
            <FieldInput label="Year"><input type="number" min="1900" max="2027" value={val('year')} onChange={set('year')} placeholder="2024" className={inputCls} data-testid="vehicle-year" /></FieldInput>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <FieldInput label="Mileage (km)"><input type="number" min="0" value={val('mileage')} onChange={set('mileage')} placeholder="0" className={inputCls} data-testid="vehicle-mileage" /></FieldInput>
            <FieldInput label="Transmission">
              <select value={val('transmission')} onChange={set('transmission')} className={selectCls} data-testid="vehicle-transmission">
                <option value="">Select...</option>
                <option value="automatic">Automatic</option>
                <option value="manual">Manual</option>
                <option value="cvt">CVT</option>
              </select>
            </FieldInput>
            <FieldInput label="Fuel Type">
              <select value={val('fuel_type')} onChange={set('fuel_type')} className={selectCls} data-testid="vehicle-fuel">
                <option value="">Select...</option>
                <option value="gas">Gas</option>
                <option value="diesel">Diesel</option>
                <option value="electric">Electric</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </FieldInput>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput label="Drivetrain">
              <select value={val('drivetrain')} onChange={set('drivetrain')} className={selectCls} data-testid="vehicle-drivetrain">
                <option value="">Select...</option>
                <option value="fwd">FWD</option>
                <option value="rwd">RWD</option>
                <option value="awd">AWD</option>
                <option value="4wd">4WD</option>
              </select>
            </FieldInput>
            <FieldInput label="Color"><input type="text" value={val('color')} onChange={set('color')} placeholder="e.g. White" className={inputCls} data-testid="vehicle-color" /></FieldInput>
          </div>
        </div>
      );

    case 'real_estate':
      return (
        <div className="bg-white rounded-lg border border-zinc-200 p-6 space-y-4" data-testid="real-estate-fields">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-rose-600 flex items-center gap-1"><Home className="w-3.5 h-3.5" /> Property Details</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <FieldInput label="Bedrooms"><input type="number" min="0" value={val('bedrooms')} onChange={set('bedrooms')} placeholder="0" className={inputCls} data-testid="re-bedrooms" /></FieldInput>
            <FieldInput label="Bathrooms"><input type="number" min="0" step="0.5" value={val('bathrooms')} onChange={set('bathrooms')} placeholder="0" className={inputCls} data-testid="re-bathrooms" /></FieldInput>
            <FieldInput label="Sq. Ft."><input type="number" min="0" value={val('square_feet')} onChange={set('square_feet')} placeholder="0" className={inputCls} data-testid="re-sqft" /></FieldInput>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <FieldInput label="Lot Size"><input type="text" value={val('lot_size')} onChange={set('lot_size')} placeholder="e.g. 50x120 ft" className={inputCls} data-testid="re-lot" /></FieldInput>
            <FieldInput label="Year Built"><input type="number" min="1800" max="2027" value={val('year_built')} onChange={set('year_built')} placeholder="2000" className={inputCls} data-testid="re-year" /></FieldInput>
            <FieldInput label="MLS #"><input type="text" value={val('mls_number')} onChange={set('mls_number')} placeholder="Optional" className={inputCls} data-testid="re-mls" /></FieldInput>
          </div>
        </div>
      );

    default:
      return null;
  }
}

export default function PostListingPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [city, setCity] = useState(user?.city || 'Pickering');
  const [condition, setCondition] = useState('good');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [images, setImages] = useState([]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [typeFields, setTypeFields] = useState({});

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth'); return; }
    metaAPI.getCategories().then(r => setCategories(r.data)).catch(() => {});
  }, [user, navigate, authLoading]);

  const selectedCategory = categories.find(c => c.id === category);
  const subcategories = selectedCategory?.subcategories || [];
  const listingType = selectedCategory?.listing_type || 'sale';

  // Reset type fields when category changes
  useEffect(() => { setTypeFields({}); }, [category]);

  const setTypeField = (key, value) => setTypeFields(prev => ({ ...prev, [key]: value }));

  const showPrice = !NO_PRICE_TYPES.includes(listingType);
  const showCondition = !NO_CONDITION_TYPES.includes(listingType);
  const showPayment = !NO_PAYMENT_TYPES.includes(listingType);

  const getPriceLabel = () => {
    if (listingType === 'rental') return 'Monthly Rent (CAD)';
    if (listingType === 'service') return 'Starting Price (CAD)';
    if (listingType === 'job') return 'Salary / Rate (CAD)';
    return 'Price (CAD)';
  };

  const getDescLabel = () => {
    if (listingType === 'job') return 'Job Description';
    if (listingType === 'service') return 'Service Description';
    if (listingType === 'event') return 'Event Description';
    if (listingType === 'lost_found') return 'Details (colour, size, distinguishing features)';
    if (listingType === 'announcement') return 'Announcement Details';
    if (listingType === 'garage_sale') return 'What are you selling? Highlights?';
    return 'Description';
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (images.length + files.length > 8) { toast.error('Maximum 8 images allowed'); return; }
    setUploading(true);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} is too large. Max 10MB`); continue; }
      try {
        const { data } = await uploadAPI.uploadImage(file);
        const serverUrl = `${BACKEND_URL}${data.url}`;
        const localPreview = URL.createObjectURL(file);
        setImages(prev => [...prev, { url: serverUrl, preview: localPreview, type: 'uploaded', filename: file.name }]);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const addImageUrl = () => {
    if (newImageUrl.trim() && images.length < 8) { setImages([...images, { url: newImageUrl.trim(), type: 'url' }]); setNewImageUrl(''); }
  };

  const removeImage = (idx) => setImages(images.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !description || !category || !city) { toast.error('Please fill in all required fields'); return; }
    if (showPrice && !price && listingType !== 'service') { toast.error('Please enter a price'); return; }
    setLoading(true);
    try {
      const { data } = await listingsAPI.create({
        title, description, price: parseFloat(price) || 0,
        category, subcategory: subcategory || null, city, condition: showCondition ? condition : null,
        payment_method: showPayment ? paymentMethod : 'none',
        images: images.map(img => img.url),
        listing_type: listingType,
        type_fields: typeFields,
      });
      toast.success('Posted successfully!');
      navigate(`/listing/${data.listing_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post listing');
    } finally { setLoading(false); }
  };

  const getSubmitLabel = () => {
    if (listingType === 'event') return 'Post Event';
    if (listingType === 'job') return 'Post Job';
    if (listingType === 'service') return 'Post Service';
    if (listingType === 'lost_found') return 'Post Lost & Found';
    if (listingType === 'announcement') return 'Post Announcement';
    if (listingType === 'garage_sale') return 'Post Garage Sale';
    if (listingType === 'rental') return 'Post Rental';
    return 'Post Listing';
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <div className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto py-8 sm:py-12">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900">Post a Listing</h1>
          <p className="text-sm text-zinc-500 mt-2">Share with your Durham Region community</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="post-listing-form">
          {/* Title */}
          <div className="bg-white rounded-lg border border-zinc-200 p-6">
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Title *</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} required
              placeholder={listingType === 'job' ? 'Job title' : listingType === 'event' ? 'Event name' : listingType === 'lost_found' ? 'e.g. Lost Golden Retriever near Whitby' : 'What are you listing?'}
              className={inputCls} data-testid="post-title-input" />
          </div>

          {/* Category + Subcategory + City */}
          <div className="bg-white rounded-lg border border-zinc-200 p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Category *</label>
                <select value={category} onChange={e => { setCategory(e.target.value); setSubcategory(''); }} required className={selectCls} data-testid="post-category-select">
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Subcategory</label>
                <select value={subcategory} onChange={e => setSubcategory(e.target.value)} disabled={!category || subcategories.length === 0}
                  className={`${selectCls} disabled:opacity-50`} data-testid="post-subcategory-select">
                  <option value="">All subcategories</option>
                  {subcategories.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">City *</label>
              <select value={city} onChange={e => setCity(e.target.value)} required className={selectCls} data-testid="post-city-select">
                {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Category-Specific Fields */}
          <TypeFields listingType={listingType} typeFields={typeFields} setTypeField={setTypeField} />

          {/* Price + Condition (when applicable) */}
          {(showPrice || showCondition) && (
            <div className="bg-white rounded-lg border border-zinc-200 p-6 grid sm:grid-cols-2 gap-4">
              {showPrice && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">{getPriceLabel()}{listingType !== 'service' && ' *'}</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <input type="number" step="0.01" min="0" value={price} onChange={e => setPrice(e.target.value)}
                      required={listingType !== 'service' && listingType !== 'event'}
                      placeholder="0.00" className={`${inputCls} pl-10`} data-testid="post-price-input" />
                  </div>
                </div>
              )}
              {showCondition && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Condition</label>
                  <select value={condition} onChange={e => setCondition(e.target.value)} className={selectCls} data-testid="post-condition-select">
                    {CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div className="bg-white rounded-lg border border-zinc-200 p-6">
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">{getDescLabel()} *</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} required rows={5}
              placeholder={listingType === 'job' ? 'Responsibilities, requirements, benefits...' : listingType === 'service' ? 'Describe your service, qualifications, what you offer...' : 'Describe your listing in detail...'}
              className={`${inputCls} resize-none`} data-testid="post-description-input" />
          </div>

          {/* Payment Method */}
          {showPayment && (
            <div className="bg-white rounded-lg border border-zinc-200 p-6">
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3 block">Payment Method</label>
              <div className="flex flex-wrap gap-3">
                {[{ value: 'cash', label: 'Cash / Meet-up' }, { value: 'stripe', label: 'Online Payment (Stripe)' }, { value: 'both', label: 'Both' }].map(opt => (
                  <button key={opt.value} type="button" onClick={() => setPaymentMethod(opt.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${paymentMethod === opt.value ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300'}`}
                    data-testid={`payment-${opt.value}-btn`}
                  >{opt.label}</button>
                ))}
              </div>
            </div>
          )}

          {/* Images */}
          <div className="bg-white rounded-lg border border-zinc-200 p-6">
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3 block">Images (up to 8)</label>
            <div className="mb-4">
              <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/jpeg,image/png,image/gif,image/webp" multiple className="hidden" data-testid="file-upload-input" />
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading || images.length >= 8}
                className="w-full flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed border-zinc-300 rounded-lg text-sm font-medium text-zinc-600 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors disabled:opacity-50"
                data-testid="upload-images-btn"
              >
                {uploading ? <><Loader2 className="w-5 h-5 animate-spin" /> Uploading...</> : <><Upload className="w-5 h-5" /> Upload from your computer</>}
              </button>
              <p className="text-xs text-zinc-400 mt-1">JPEG, PNG, GIF, WebP up to 10MB each</p>
            </div>
            <div className="flex gap-2 mb-4">
              <input type="url" value={newImageUrl} onChange={e => setNewImageUrl(e.target.value)} placeholder="Or paste an image URL..."
                className={`flex-1 ${inputCls}`} data-testid="post-image-url-input" />
              <button type="button" onClick={addImageUrl} disabled={!newImageUrl.trim() || images.length >= 8}
                className="px-4 py-2.5 bg-zinc-100 text-zinc-700 rounded-lg text-sm font-medium hover:bg-zinc-200 disabled:opacity-50"
                data-testid="add-image-btn"
              ><ImagePlus className="w-4 h-4" /></button>
            </div>
            {images.length > 0 && (
              <div className="grid grid-cols-4 gap-2" data-testid="image-preview-grid">
                {images.map((img, i) => (
                  <div key={i} className="relative aspect-square rounded-md overflow-hidden bg-zinc-100 group">
                    <img src={img.preview || img.url} alt={img.filename || ''} className="w-full h-full object-cover" />
                    <button onClick={() => removeImage(i)} className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${i}`}><X className="w-3 h-3" /></button>
                    {img.type === 'uploaded' && <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-emerald-500 text-white rounded text-[9px] font-bold">Uploaded</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <button type="submit" disabled={loading}
            className="w-full bg-[#002FA7] text-white hover:bg-[#002585] transition-colors rounded-full py-3.5 font-bold text-sm disabled:opacity-50"
            data-testid="post-submit-btn"
          >{loading ? 'Posting...' : getSubmitLabel()}</button>
        </form>
      </div>
    </div>
  );
}
